import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import type { ClassNameProp } from '..';
import { HBox } from '..';

/**
 * Properties used for a {@link Timer}.
 */

export interface TimerProps extends ClassNameProp {
  /**
   * The name of timer that appears on the left side of the element
   */
  timerName?: string;

  /**
   * Message that should be shown when the timer zeroes out
   */
  timeoutMessage: string;
  /**
   * The timer will count from or to this target date
   */
  targetDate: Date | null;
  /**
   * callback function which will be invoked when the timer stops
   */
  onTimerStop?: () => void;

  /**
   * Only Returns the timer text.
   */
  textOnly?: boolean;
}

/**
 * Component for displaying a timer or countdown in gray ellipse
 * @constructor
 * @return the created {@link JSX.Element}
 * @param targetDate
 * @param currentDate
 */

function calculateTimeDiff(targetDate: Date | null, currentDate: Date) {
  if (!targetDate) {
    return {
      diffSeconds: undefined,
      diffMinutes: undefined,
      diffHours: undefined
    };
  }

  const delta = Math.abs(targetDate?.getTime() - currentDate.getTime());
  return {
    diffSeconds: Math.floor((delta / 1000) % 60),
    diffMinutes: Math.floor((delta / (1000 * 60)) % 60),
    diffHours: Math.floor(delta / (1000 * 60 * 60))
  };
}

export type FormattedTimerMessageProps = {
  hours: number;
  minutes: number;
  seconds: number;
  timeoutMessage: string;
};

export function FormattedTimerMessage({
  hours,
  minutes,
  seconds,
  timeoutMessage,
  ...props
}: Readonly<FormattedTimerMessageProps>): JSX.Element {
  return (
    <span {...props} className="ehs-timerKeeper">
      {hours === 0 && minutes === 0 && seconds === 0 && timeoutMessage ? (
        timeoutMessage
      ) : (
        <>
          {hours > 0 && `${hours}:`}
          {hours > 0 &&
            minutes === 0 &&
            `${('0' + minutes).slice(-2)}:${('0' + seconds).slice(-2)}`}
          {minutes > 0 &&
            `${('0' + minutes).slice(-2)}:${('0' + seconds).slice(-2)}`}
          {hours === 0 && minutes === 0 && `${('0' + seconds).slice(-2)}s`}
        </>
      )}
    </span>
  );
}

export function Timer({
  timerName,
  timeoutMessage,
  targetDate,
  onTimerStop,
  className = '',
  textOnly = false,
  ...props
}: Readonly<TimerProps>): JSX.Element {
  const [timer, setTimer] = useState<{
    seconds?: number;
    minutes?: number;
    hours?: number;
  }>({
    seconds: undefined,
    minutes: undefined,
    hours: undefined
  });

  const intervalId = useRef<number | undefined>();

  const setTimerFunction = useCallback(
    (clearInteval: boolean) => {
      const currentDate = new Date();
      const { diffSeconds, diffMinutes, diffHours } = calculateTimeDiff(
        targetDate,
        currentDate
      );
      setTimer(() => ({
        seconds: diffSeconds,
        minutes: diffMinutes,
        hours: diffHours
      }));

      if (
        diffSeconds === 0 &&
        diffMinutes === 0 &&
        diffHours === 0 &&
        intervalId.current
      ) {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        clearInteval && clearInterval(intervalId.current);
        if (onTimerStop) {
          onTimerStop();
        }
      }
    },
    [onTimerStop, targetDate]
  );

  const interval = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    intervalId.current = setInterval(() => {
      setTimerFunction(true);
    }, 1000);
  }, [setTimerFunction]);

  useEffect(() => {
    interval();
    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
      }
    };
  }, [interval]);

  useLayoutEffect(() => {
    setTimerFunction(false);
  }, [onTimerStop, setTimerFunction, targetDate]);

  const { seconds, minutes, hours } = timer;
  if (
    textOnly &&
    hours !== undefined &&
    minutes !== undefined &&
    seconds !== undefined
  ) {
    return (
      <FormattedTimerMessage
        {...props}
        hours={hours}
        minutes={minutes}
        seconds={seconds}
        timeoutMessage={timeoutMessage}
      />
    );
  } else {
    return (
      <HBox {...props} className={`ehs-timer ${className}`.trim()}>
        <div className="ovalTimer">
          {timerName && (
            <p
              className={
                targetDate ? 'innerTimerText' : 'innerTimerText stand-alone'
              }
            >
              {timerName}
            </p>
          )}

          <div className="innerTimer borderCircle" role="timer">
            {hours !== undefined &&
              minutes !== undefined &&
              seconds !== undefined &&
              hours + minutes + seconds >= 0 && (
                <FormattedTimerMessage
                  {...props}
                  hours={hours}
                  minutes={minutes}
                  seconds={seconds}
                  timeoutMessage={timeoutMessage}
                />
              )}
          </div>
        </div>
      </HBox>
    );
  }
}
